import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { App as AntApp, Dropdown } from 'antd';
import AppBar from '../components/AppBar';
import InfoNotes from '../components/InfoNotes';
import { RM } from '../components/kit';
import { computeMortgage, amortizationSchedule, num } from '../lib/loanCalc';

// Mortgage RESULT page — /tools/mortgage/result, arrives with { mode, f } in router
// state and recomputes via the shared loanCalc engine (single source of truth with
// the input page). Layout follows the reference sections: header stats, LOAN
// SUMMARY, CASH REQUIRED, PRICE/AREA, INVESTMENT, notes. The ⋮ menu holds the
// amortisation table and Share result.

const SQFT_PER_SQM = 10.7639;
const fmt2 = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const STATUS = { free: 'FREE', exempt: 'EXEMPT', financed: 'FINANCED' };

export default function MortgageResultPage() {
  const nav = useNavigate();
  const { message } = AntApp.useApp();
  const { state } = useLocation();
  const [showAmort, setShowAmort] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  const mode = state?.mode;
  const f = state?.f;
  const r = useMemo(() => (f ? computeMortgage({
    mode, ...f,
    area: f.areaUnit === 'sqm' ? num(f.area) * SQFT_PER_SQM : f.area,
  }) : null), [mode, f]);

  if (!f || !r || r.price <= 0) {
    // Deep-linked or refreshed without inputs — back to the calculator.
    nav('/tools/mortgage', { replace: true });
    return null;
  }

  const areaSqft = f.areaUnit === 'sqm' ? num(f.area) * SQFT_PER_SQM : num(f.area);
  const nMonths = Math.round(num(f.years) * 12);
  const loanPctOfPayment = r.totalPayment > 0 ? (r.facility / r.totalPayment) * 100 : 0;
  const interestPctOfPayment = r.totalPayment > 0 ? (r.totalInterest / r.totalPayment) * 100 : 0;
  const rentAnnual = num(f.rent) * 12;
  const utilAnnual = num(f.utilities) * 12;

  const share = async () => {
    const lines = [
      `CID Mortgage estimate — ${mode === 'NEW' ? 'Pre/New Launch' : 'Subsale'}`,
      `Purchase price: RM ${fmt2(r.price)}`,
      `Loan: RM ${fmt2(r.facility)} @ ${num(f.ratePct).toFixed(2)}% · ${num(f.years)} years`,
      `Monthly instalment: RM ${fmt2(r.installment)}`,
      `Total interest: RM ${fmt2(r.totalInterest)} · Total payment: RM ${fmt2(r.totalPayment)}`,
      `Total cash required: RM ${fmt2(r.entryTotal)}`,
    ].join('\n');
    try {
      if (navigator.share) { await navigator.share({ text: lines }); return; }
      await navigator.clipboard.writeText(lines);
      message.success('Result copied to clipboard');
    } catch (_) { /* share cancelled */ }
  };

  const menu = {
    items: [
      { key: 'amort', label: 'Amortization' },
      { key: 'share', label: 'Share result' },
    ],
    onClick: ({ key }) => (key === 'amort' ? setShowAmort(true) : share()),
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        back
        title={mode === 'NEW' ? 'Pre/ New Launch' : 'Subsale'}
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div onClick={() => setInfoOpen(true)} style={{ width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--muted)' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><line x1="12" y1="11" x2="12" y2="16" /><circle cx="12" cy="7.6" r="0.4" fill="currentColor" /></svg>
          </div>
          <Dropdown trigger={['click']} menu={menu}>
            <div style={{ width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '1px solid var(--border)', background: 'var(--surface)' }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.9" /><circle cx="12" cy="12" r="1.9" /><circle cx="12" cy="19" r="1.9" /></svg>
            </div>
          </Dropdown>
          </div>
        }
      />

      {/* header stats */}
      <div style={{ display: 'flex', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        <Stat label="Total Cash Required" value={`RM${fmt2(r.entryTotal)}`} />
        <div style={{ width: 1, background: 'var(--border)', margin: '10px 0' }} />
        <Stat label="Monthly Instalment" value={`RM${fmt2(r.installment)}`} />
      </div>

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Block title="LOAN SUMMARY">
          <Row label="Loan Terms" value={`${num(f.ratePct).toFixed(2)}% @ ${num(f.years)} years`} />
          <Row label="Purchase Price" value={fmt2(r.price)} />
          {mode === 'NEW' && (
            <Row label={`Developer Discount ${num(f.discountPct).toFixed(2)}%`} value={fmt2(r.price - r.netPrice)} />
          )}
          <Row label="Total Loan Amount" value={`${loanPctOfPayment.toFixed(2)}% • ${fmt2(r.facility)}`} />
          <Row label="Total Interest Payable" value={`${interestPctOfPayment.toFixed(2)}% • ${fmt2(r.totalInterest)}`} />
          <Row label="Total Payment" value={fmt2(r.totalPayment)} strong />
        </Block>

        <Block title="CASH REQUIRED">
          {r.items.map((it) => (
            <Row
              key={it.key}
              label={it.label}
              value={it.status === 'pay' ? fmt2(it.amount) : `${fmt2(it.amount)} · ${STATUS[it.status]}`}
              dim={it.status !== 'pay'}
            />
          ))}
          <Row label={`Down Payment ${f.downMode === '%' ? `${num(f.downValue).toFixed(2)}%` : ''}`} value={fmt2(r.down)} />
          {r.financedCosts > 0 && <Row label="Costs financed into the loan" value={fmt2(r.financedCosts)} dim />}
          <Row label="Total Cash Required" value={fmt2(r.entryTotal)} strong />
        </Block>

        <Block title="PRICE/ AREA">
          <Row label={`${fmt2(areaSqft)} sqft`} value={`= ${fmt2(areaSqft / SQFT_PER_SQM)} sqm`} />
          <Row
            label={`RM${fmt2(areaSqft > 0 ? r.netPrice / areaSqft : 0)}/ sqft`}
            value={`RM${fmt2(areaSqft > 0 ? (r.netPrice / areaSqft) * SQFT_PER_SQM : 0)}/ sqm`}
          />
        </Block>

        <Block title="INVESTMENT">
          <Row label="Annual Gross Rental Income" value={fmt2(rentAnnual)} />
          <Row label="Annual Utilities & Fees" value={fmt2(utilAnnual)} />
          <Row label="Rental/ sqft" value={`RM${fmt2(areaSqft > 0 ? num(f.rent) / areaSqft : 0)}/ sqft`} />
          <Row label="Gross Rental Yield" value={`${(r.grossYieldPct ?? 0).toFixed(2)}% p.a`} />
          <Row
            label={(r.cashFlow ?? -r.installment) >= 0 ? 'Positive Cash Flow' : 'Negative Cash Flow'}
            value={`RM${fmt2(Math.abs(r.cashFlow ?? -r.installment))}/ month`}
            tone={(r.cashFlow ?? -r.installment) >= 0 ? 'var(--ok)' : 'var(--bad)'}
            strong
          />
        </Block>
      </div>

      <InfoNotes
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title="Mortgage notes"
        pages={[
          { title: 'Fees & duty', body: '1. Legal fees follow the Solicitors\u2019 Remuneration Order 2023 scale \u2014 1.25% on the first RM500k (minimum RM500), 1% thereafter \u2014 applied to the SPA (on price) and the loan facility (on loan amount). SST and actual disbursements vary by firm.\n\n2. Stamp duty (MOT/DOA): 1% on the first RM100k, 2% on RM100k\u2013500k, 3% on RM500k\u20131m, 4% above RM1m. Loan agreement stamp duty: 0.5% of the facility.\n\n* Estimated RM2,500 per side for solicitor disbursements & misc charges.' },
          { title: 'Exemptions', body: '3. First-time home buyer (Budget 2026): 100% MOT + loan stamp-duty exemption for residential homes \u2264 RM500k (SPA 1 Jan 2026 \u2013 31 Dec 2027).\n\n4. For new-launch purchases, fees marked FREE are absorbed under the developer\u2019s package; for high-rise, MOT/strata transfer may fall due later at title issuance.\n\n5. Subsale bank valuation fee follows the Valuers\u2019 (LPPEH) scale, minimum RM400; no valuation fee when buying directly from a developer.' },
          { title: 'Budgeting', body: '6. Budget for other costs: MRTA/MLTA, fire insurance, utility deposits, renovation and maintenance.\n\nAll figures are estimates \u2014 nothing is saved.' },
        ]}
      />

      {showAmort && (
        <Amortization
          installment={r.installment}
          months={nMonths}
          schedule={amortizationSchedule(r.facility, f.ratePct, f.years)}
          onClose={() => setShowAmort(false)}
        />
      )}
    </div>
  );
}

// ─── amortization table (full-screen) ─────────────────────────────────────
function Amortization({ installment, months, schedule, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 4600, background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 15px', paddingTop: 'calc(10px + env(safe-area-inset-top))', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        <div onClick={onClose} style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flex: 'none' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 5 8 12 15 19" /></svg>
        </div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{RM(installment)} · {months} months</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '52px 1fr 1fr 1.2fr', gap: 0, padding: '8px 14px', fontSize: 11.5, fontWeight: 600, color: 'var(--muted)', borderBottom: '1px solid var(--border)', background: 'var(--surface)', flex: 'none' }}>
        <span>Month</span><span style={{ textAlign: 'right' }}>Principal</span><span style={{ textAlign: 'right' }}>Interest</span><span style={{ textAlign: 'right' }}>Balance</span>
      </div>
      <div className="scrx" style={{ flex: 1, overflowY: 'auto' }}>
        {schedule.map((row) => (
          <div key={row.month} style={{ display: 'grid', gridTemplateColumns: '52px 1fr 1fr 1.2fr', padding: '7px 14px', fontSize: 12.5, fontVariantNumeric: 'tabular-nums', background: row.month % 2 ? 'var(--surface)' : 'transparent' }}>
            <span>{row.month}</span>
            <span style={{ textAlign: 'right' }}>{fmt2(row.principal)}</span>
            <span style={{ textAlign: 'right' }}>{fmt2(row.interest)}</span>
            <span style={{ textAlign: 'right' }}>{fmt2(row.balance)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div style={{ flex: 1, padding: '14px 16px' }}>
    <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{label}</div>
    <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>{value}</div>
  </div>
);

const Block = ({ title, children }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
    <div style={{ padding: '10px 14px 4px', fontSize: 12.5, fontWeight: 700, letterSpacing: '.04em', color: 'var(--primary)' }}>{title}</div>
    {children}
  </div>
);

const Row = ({ label, value, strong, dim, tone }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '14px 16px', borderTop: '1px solid var(--border)', fontSize: 13, background: strong ? 'var(--chip)' : undefined }}>
    <span style={{ color: dim ? 'var(--muted)' : 'var(--text)', fontWeight: strong ? 600 : 400 }}>{label}</span>
    <b style={{ color: tone || (dim ? 'var(--muted)' : strong ? 'var(--primary)' : 'var(--text)'), textDecoration: dim ? 'line-through' : undefined, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{value}</b>
  </div>
);
